import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Form, FormField, FormItem, FormLabel, FormControl } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";

type Account = {
  company_id?: string;
  region: string;
  country: string;
  company_name: string;
  parent_company?: string;
  industry: string;
  account_manager: string;
};

const regions = ["North America", "South America", "Europe", "Asia Pacific", "Middle East & Africa"];
const countries = {
  "North America": ["United States", "Canada", "Mexico"],
  "South America": ["Brazil", "Argentina", "Colombia", "Chile"],
  "Europe": ["United Kingdom", "Germany", "France", "Italy", "Spain", "Netherlands"],
  "Asia Pacific": ["China", "Japan", "South Korea", "Taiwan", "Hong Kong", "Australia", "India", "Singapore", "Malaysia", "Indonesia", "Thailand", "Philippines"],
  "Middle East & Africa": ["UAE", "Saudi Arabia", "South Africa", "Israel", "Egypt"]
} as const;
const industries = ["Technology", "Financial Services", "Healthcare", "Manufacturing", "Retail", "Telecommunications", "Energy", "Automotive", "Consumer Goods", "Professional Services"];

const AccountTab = ({
  onAccountSelect,
  selectedAccount_row,
  setSelectedAccount_row
}: {
  onAccountSelect: (account: Account | null) => void;
  selectedAccount_row: number | null;
  setSelectedAccount_row: (row: number | null) => void;
}) => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<string | undefined>(undefined);
  const [isDeleting, setIsDeleting] = useState(false);

  const form = useForm<Account>({
    defaultValues: {
      region: "",
      country: "",
      company_name: "",
      parent_company: "",
      industry: "",
      account_manager: ""
    }
  });

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (accounts.length > 0 && selectedAccount_row !== null) {
      setSelectedAccount(accounts[selectedAccount_row]);
    }
  }, [accounts, selectedAccount_row]);

  useEffect(() => {
    onAccountSelect(selectedAccount);
  }, [selectedAccount, onAccountSelect]);

  const fetchAccounts = async () => {
    try {
      const {
        data: accountData,
        error
      } = await supabase.from("accounts").select("*").order("country", {
        ascending: true
      });

      if (error) {
        console.error('Error fetching accounts:', error);
        return;
      }

      setAccounts(accountData || []);
    } catch (error) {
      console.error('Error in fetchAccounts:', error);
    }
  };

  const onSubmit = async (data: Account) => {
    try {
      if (editingAccount) {
        const {
          error
        } = await supabase.from("accounts").update({
          region: data.region,
          country: data.country,
          company_name: data.company_name,
          parent_company: data.parent_company,
          industry: data.industry,
          account_manager: data.account_manager,
          updated_at: new Date().toISOString()
        }).eq("company_id", editingAccount.company_id);

        if (error) {
          console.error('Error updating account:', error);
          return;
        }
      } else {
        const {
          error
        } = await supabase.from("accounts").insert([{
          ...data,
          user_id: (await supabase.auth.getUser()).data.user?.id
        }]);

        if (error) {
          console.error('Error creating account:', error);
          return;
        }
      }

      setIsAddDialogOpen(false);
      form.reset();
      setSelectedRegion("");
      fetchAccounts();
    } catch (error) {
      console.error('Error in onSubmit:', error);
    }
  };

  const handleEdit = (account: Account) => {
    setEditingAccount(account);
    form.reset(account);
    setSelectedRegion(account.region);
    setIsAddDialogOpen(true);
  };

  const cascadeDeleteAccount = async (company_id: string) => {
    setIsDeleting(true);
    
    try {
      console.log(`Starting cascading delete for company_id: ${company_id}`);
      
      let deletedCounts: Record<string, number> = {};

      // Delete from key_asks table
      try {
        const { data: keyAsksData, error: keyAsksError } = await supabase
          .from('key_asks')
          .delete()
          .eq('company_id', company_id)
          .select();

        if (keyAsksError) {
          console.error('Error deleting from key_asks:', keyAsksError);
          toast.error('Failed to delete key asks records');
          return;
        }

        deletedCounts['key_asks'] = keyAsksData?.length || 0;
        console.log(`Deleted ${deletedCounts['key_asks']} records from key_asks`);
      } catch (error) {
        console.error('Error processing key_asks table:', error);
        toast.error('Error processing key asks table');
        return;
      }

      // Delete from customer_kpis table
      try {
        const { data: customerKpisData, error: customerKpisError } = await supabase
          .from('customer_kpis')
          .delete()
          .eq('company_id', company_id)
          .select();

        if (customerKpisError) {
          console.error('Error deleting from customer_kpis:', customerKpisError);
          toast.error('Failed to delete customer KPIs records');
          return;
        }

        deletedCounts['customer_kpis'] = customerKpisData?.length || 0;
        console.log(`Deleted ${deletedCounts['customer_kpis']} records from customer_kpis`);
      } catch (error) {
        console.error('Error processing customer_kpis table:', error);
        toast.error('Error processing customer KPIs table');
        return;
      }

      // Delete from virtual_account_team table
      try {
        const { data: virtualTeamData, error: virtualTeamError } = await supabase
          .from('virtual_account_team')
          .delete()
          .eq('company_id', company_id)
          .select();

        if (virtualTeamError) {
          console.error('Error deleting from virtual_account_team:', virtualTeamError);
          toast.error('Failed to delete virtual account team records');
          return;
        }

        deletedCounts['virtual_account_team'] = virtualTeamData?.length || 0;
        console.log(`Deleted ${deletedCounts['virtual_account_team']} records from virtual_account_team`);
      } catch (error) {
        console.error('Error processing virtual_account_team table:', error);
        toast.error('Error processing virtual account team table');
        return;
      }

      // Delete from deals_pipeline table
      try {
        const { data: dealsData, error: dealsError } = await supabase
          .from('deals_pipeline')
          .delete()
          .eq('company_id', company_id)
          .select();

        if (dealsError) {
          console.error('Error deleting from deals_pipeline:', dealsError);
          toast.error('Failed to delete deals pipeline records');
          return;
        }

        deletedCounts['deals_pipeline'] = dealsData?.length || 0;
        console.log(`Deleted ${deletedCounts['deals_pipeline']} records from deals_pipeline`);
      } catch (error) {
        console.error('Error processing deals_pipeline table:', error);
        toast.error('Error processing deals pipeline table');
        return;
      }

      // Delete from business_value_framework table
      try {
        const { data: bvfData, error: bvfError } = await supabase
          .from('business_value_framework')
          .delete()
          .eq('company_id', company_id)
          .select();

        if (bvfError) {
          console.error('Error deleting from business_value_framework:', bvfError);
          toast.error('Failed to delete business value framework records');
          return;
        }

        deletedCounts['business_value_framework'] = bvfData?.length || 0;
        console.log(`Deleted ${deletedCounts['business_value_framework']} records from business_value_framework`);
      } catch (error) {
        console.error('Error processing business_value_framework table:', error);
        toast.error('Error processing business value framework table');
        return;
      }

      // Delete from org_chart_relationships table
      try {
        const { data: orgChartData, error: orgChartError } = await supabase
          .from('org_chart_relationships')
          .delete()
          .eq('company_id', company_id)
          .select();

        if (orgChartError) {
          console.error('Error deleting from org_chart_relationships:', orgChartError);
          toast.error('Failed to delete org chart relationships records');
          return;
        }

        deletedCounts['org_chart_relationships'] = orgChartData?.length || 0;
        console.log(`Deleted ${deletedCounts['org_chart_relationships']} records from org_chart_relationships`);
      } catch (error) {
        console.error('Error processing org_chart_relationships table:', error);
        toast.error('Error processing org chart relationships table');
        return;
      }

      // Delete from customer_contacts table
      try {
        const { data: contactsData, error: contactsError } = await supabase
          .from('customer_contacts')
          .delete()
          .eq('company_id', company_id)
          .select();

        if (contactsError) {
          console.error('Error deleting from customer_contacts:', contactsError);
          toast.error('Failed to delete customer contacts records');
          return;
        }

        deletedCounts['customer_contacts'] = contactsData?.length || 0;
        console.log(`Deleted ${deletedCounts['customer_contacts']} records from customer_contacts`);
      } catch (error) {
        console.error('Error processing customer_contacts table:', error);
        toast.error('Error processing customer contacts table');
        return;
      }

      // Delete from finance_summary table
      try {
        const { data: financeData, error: financeError } = await supabase
          .from('finance_summary')
          .delete()
          .eq('company_id', company_id)
          .select();

        if (financeError) {
          console.error('Error deleting from finance_summary:', financeError);
          toast.error('Failed to delete finance summary records');
          return;
        }

        deletedCounts['finance_summary'] = financeData?.length || 0;
        console.log(`Deleted ${deletedCounts['finance_summary']} records from finance_summary`);
      } catch (error) {
        console.error('Error processing finance_summary table:', error);
        toast.error('Error processing finance summary table');
        return;
      }

      // Finally, delete from accounts table
      try {
        const { data: accountsData, error: accountsError } = await supabase
          .from('accounts')
          .delete()
          .eq('company_id', company_id)
          .select();

        if (accountsError) {
          console.error('Error deleting from accounts:', accountsError);
          toast.error('Failed to delete account record');
          return;
        }

        deletedCounts['accounts'] = accountsData?.length || 0;
        console.log(`Deleted ${deletedCounts['accounts']} records from accounts`);
      } catch (error) {
        console.error('Error processing accounts table:', error);
        toast.error('Error processing accounts table');
        return;
      }

      const totalDeleted = Object.values(deletedCounts).reduce((sum, count) => sum + count, 0);
      
      if (totalDeleted > 0) {
        toast.success(`Successfully deleted ${totalDeleted} records across all Sales module tables`);
        console.log('Deletion summary:', deletedCounts);
      } else {
        toast.info('No records found to delete');
      }

      // Clear selected account if it was the deleted one
      if (selectedAccount?.company_id === company_id) {
        setSelectedAccount(null);
        setSelectedAccount_row(null);
        onAccountSelect(null);
      }

      // Refresh accounts list
      await fetchAccounts();
      
    } catch (error) {
      console.error('Error in cascadeDeleteAccount:', error);
      toast.error('An unexpected error occurred during deletion');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDelete = async (company_id: string) => {
    if (!company_id) {
      toast.error('Invalid company ID');
      return;
    }
    
    await cascadeDeleteAccount(company_id);
  };

  // Reduce the number of empty rows from 15 to 3
  const emptyRows = Array.from({
    length: 3
  }, (_, index) => index);

  return <Card className="bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10 h-[calc(100vh-12rem)] py-[9px] my-[12px]">
      <CardContent className="mt-4 flex flex-col h-full">
        <div className="flex justify-between items-center mb-4">
          <div className="space-y-1">
            <h2 className="text-xl font-semibold text-white">Accounts</h2>
            {selectedAccount && <p className="text-[11px] text-primary-light">Selected Account ID: {selectedAccount.company_id}</p>}
          </div>
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-primary/20 bg-primary hover:bg-primary/90 text-black" onClick={() => {
              setEditingAccount(null);
              form.reset();
              setSelectedRegion("");
            }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Account
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white text-black">
              <DialogHeader>
                <DialogTitle className="text-black">{editingAccount ? "Edit Account" : "Add New Account"}</DialogTitle>
              </DialogHeader>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField control={form.control} name="region" render={({
                  field
                }) => <FormItem>
                        <FormLabel className="text-black">Region</FormLabel>
                        <Select onValueChange={value => {
                    field.onChange(value);
                    setSelectedRegion(value);
                    form.setValue('country', '');
                  }} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="bg-white text-black border-gray-300">
                              <SelectValue placeholder="Select Region" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-white">
                            {regions.map(region => <SelectItem key={region} value={region} className="text-black">
                                {region}
                              </SelectItem>)}
                          </SelectContent>
                        </Select>
                      </FormItem>} />
                  <FormField control={form.control} name="country" render={({
                  field
                }) => <FormItem>
                        <FormLabel className="text-black">Country</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value} disabled={!selectedRegion}>
                          <FormControl>
                            <SelectTrigger className="bg-white text-black border-gray-300">
                              <SelectValue placeholder="Select Country" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-white">
                            {selectedRegion && countries[selectedRegion as keyof typeof countries].map(country => <SelectItem key={country} value={country} className="text-black">
                                {country}
                              </SelectItem>)}
                          </SelectContent>
                        </Select>
                      </FormItem>} />
                  <FormField control={form.control} name="company_name" render={({
                  field
                }) => <FormItem>
                        <FormLabel className="text-black">Company Name</FormLabel>
                        <FormControl>
                          <Input {...field} className="bg-white text-black border-gray-300" />
                        </FormControl>
                      </FormItem>} />
                  <FormField control={form.control} name="parent_company" render={({
                  field
                }) => <FormItem>
                        <FormLabel className="text-black">Parent Company</FormLabel>
                        <FormControl>
                          <Input {...field} className="bg-white text-black border-gray-300" />
                        </FormControl>
                      </FormItem>} />
                  <FormField control={form.control} name="industry" render={({
                  field
                }) => <FormItem>
                        <FormLabel className="text-black">Industry</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="bg-white text-black border-gray-300">
                              <SelectValue placeholder="Select Industry" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-white">
                            {industries.map(industry => <SelectItem key={industry} value={industry} className="text-black">
                                {industry}
                              </SelectItem>)}
                          </SelectContent>
                        </Select>
                      </FormItem>} />
                  <FormField control={form.control} name="account_manager" render={({
                  field
                }) => <FormItem>
                        <FormLabel className="text-black">Account Manager</FormLabel>
                        <FormControl>
                          <Input {...field} className="bg-white text-black border-gray-300" />
                        </FormControl>
                      </FormItem>} />
                  <Button type="submit" className="w-full bg-primary text-black hover:bg-primary/90">
                    {editingAccount ? "Save Changes" : "Add Account"}
                  </Button>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex-1 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-primary/30 text-[11px] hover:bg-primary/30">
                <TableHead className="text-primary-light w-[5%] text-left">SN</TableHead>
                <TableHead className="text-primary-light w-[10%] text-left">Region</TableHead>
                <TableHead className="text-primary-light w-[10%] text-left">Country</TableHead>
                <TableHead className="text-primary-light w-[20%] text-left">Company Name</TableHead>
                <TableHead className="text-primary-light w-[15%] text-left">Parent Company</TableHead>
                <TableHead className="text-primary-light w-[15%] text-left">Industry</TableHead>
                <TableHead className="text-primary-light w-[15%] text-left">Account Manager</TableHead>
                <TableHead className="text-primary-light w-[10%] text-left">Actions</TableHead>
              </TableRow>
            </TableHeader>
          </Table>
          
          <ScrollArea className="h-[calc(100%-40px)]">
            <Table>
              <TableBody>
                {accounts.map((account, index) => (
                  <TableRow
                    key={account.company_id}
                    className={`bg-secondary-light/5 text-[11px] text-left cursor-pointer ${
                      selectedAccount?.company_id === account.company_id ? 'bg-white' : ''
                    }`}
                    onClick={() => {
                      setSelectedAccount(account);
                      setSelectedAccount_row(index);
                    }}
                  >
                    <TableCell className={`w-[5%] ${selectedAccount?.company_id === account.company_id ? 'text-black' : 'text-white'}`}>
                      {index + 1}
                    </TableCell>
                    <TableCell className={`w-[10%] ${selectedAccount?.company_id === account.company_id ? 'text-black' : 'text-white'}`}>
                      {account.region}
                    </TableCell>
                    <TableCell className={`w-[10%] ${selectedAccount?.company_id === account.company_id ? 'text-black' : 'text-white'}`}>
                      {account.country}
                    </TableCell>
                    <TableCell className={`w-[20%] ${selectedAccount?.company_id === account.company_id ? 'text-black' : 'text-white'}`}>
                      {account.company_name}
                    </TableCell>
                    <TableCell className={`w-[15%] ${selectedAccount?.company_id === account.company_id ? 'text-black' : 'text-white'}`}>
                      {account.parent_company}
                    </TableCell>
                    <TableCell className={`w-[15%] ${selectedAccount?.company_id === account.company_id ? 'text-black' : 'text-white'}`}>
                      {account.industry}
                    </TableCell>
                    <TableCell className={`w-[15%] ${selectedAccount?.company_id === account.company_id ? 'text-black' : 'text-white'}`}>
                      {account.account_manager}
                    </TableCell>
                    <TableCell className={`w-[10%] ${selectedAccount?.company_id === account.company_id ? 'text-black' : 'text-white'}`}>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(account);
                          }}
                          className="h-8 w-8 text-primary hover:text-white hover:bg-primary"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAccountToDelete(account.company_id);
                            setIsDeleteDialogOpen(true);
                          }}
                          className="h-8 w-8 text-primary hover:text-white hover:bg-primary"
                          disabled={isDeleting}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {emptyRows.map((index) => (
                  <TableRow key={`empty-${index}`} className="bg-secondary-light/5">
                    <TableCell className="text-white text-[12px]">&nbsp;</TableCell>
                    <TableCell className="text-white text-[12px]">&nbsp;</TableCell>
                    <TableCell className="text-white text-[12px]">&nbsp;</TableCell>
                    <TableCell className="text-white text-[12px]">&nbsp;</TableCell>
                    <TableCell className="text-white text-[12px]">&nbsp;</TableCell>
                    <TableCell className="text-white text-[12px]">&nbsp;</TableCell>
                    <TableCell className="text-white text-[12px]">&nbsp;</TableCell>
                    <TableCell className="text-white text-[12px]">&nbsp;</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </div>
        
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Account and All Related Data</AlertDialogTitle>
              <AlertDialogDescription>
                This action will permanently delete the account and ALL related records across the entire Sales module, including:
                <br />• Customer contacts and organization chart
                <br />• Business value framework data
                <br />• Deals pipeline and key requests
                <br />• Customer KPIs and financial data
                <br />• Virtual account team information
                <br /><br />
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel 
                onClick={() => setIsDeleteDialogOpen(false)}
                disabled={isDeleting}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={() => {
                  if (accountToDelete) {
                    handleDelete(accountToDelete);
                    setIsDeleteDialogOpen(false);
                  }
                }} 
                className="bg-rose-600 hover:bg-rose-500"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete All Data'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>;
};

export default AccountTab;
